import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Hourglass, PackagePlus, Plus, X, AlertTriangle, Edit3, ChevronDown, ChevronRight } from "lucide-react";
import BackorderFormDialog from "@/components/BackorderFormDialog";

/**
 * Dashboard widget — flags incoming items on backorder so they don't fall
 * off the radar. Manager-add via "+ New". Each row has a one-tap
 * "Receive now" that launches the Receive page with backorder_id preloaded
 * for fast intake.
 *
 * The Dashboard drives collapse state so the widget stays in sync with the
 * "Backorders" stat tile (tapping the tile expands this section and scrolls
 * to it). Pass `expanded` + `onToggle` for controlled mode; if omitted the
 * widget defaults to always-expanded (unchanged legacy behaviour).
 * `onSummaryChange` bubbles the fetched counts up so the tile stays fresh.
 */
const BackordersWidget = ({ expanded, onToggle, onSummaryChange }) => {
  const controlled = typeof expanded === "boolean";
  const [selfExpanded, setSelfExpanded] = useState(true);
  const isExpanded = controlled ? expanded : selfExpanded;
  const toggle = controlled ? onToggle : () => setSelfExpanded((v) => !v);
  const nav = useNavigate();
  const { isManager } = useAuth();
  const [data, setData] = useState(null);
  const [editing, setEditing] = useState(null);
  const [formOpen, setFormOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/backorders/dashboard/summary");
      setData(data);
      onSummaryChange?.({
        open_count: data.open_count || 0,
        overdue_count: data.overdue_count || 0,
      });
    } catch (_) {
      /* transient */
    }
  }, [onSummaryChange]);

  useEffect(() => {
    load();
  }, [load]);

  const cancel = async (b) => {
    if (!window.confirm(`Cancel backorder for ${b.stock_name}?`)) return;
    try {
      await api.put(`/backorders/${b.id}`, { status: "cancelled" });
      toast.success("Backorder cancelled");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not cancel");
    }
  };

  if (!data) return null;
  const open = data.open_count || 0;
  const overdue = data.overdue_count || 0;

  return (
    <div className="nb-card p-5" data-testid="dash-backorders">
      <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
        <button
          type="button"
          data-testid="dash-backorders-toggle"
          onClick={toggle}
          className="flex items-center gap-2 flex-1 min-w-0 text-left"
          aria-expanded={isExpanded}
        >
          {isExpanded ? (
            <ChevronDown size={18} strokeWidth={2.5} />
          ) : (
            <ChevronRight size={18} strokeWidth={2.5} />
          )}
          <Hourglass size={20} strokeWidth={2.5} className="text-[#7A4A00]" />
          <h3 className="font-display text-xl">Backorders</h3>
          {open > 0 && (
            <span
              data-testid="dash-backorders-open-count"
              className="label-eyebrow px-2 py-0.5 rounded-full border border-black/30 bg-[#FFE9C2] text-[#7A4A00]"
            >
              {open} open
            </span>
          )}
          {overdue > 0 && (
            <span
              data-testid="dash-backorders-overdue-count"
              className="label-eyebrow px-2 py-0.5 rounded-full border border-black/30 bg-[#FCE4E4] text-[#8A1F1F]"
            >
              <AlertTriangle size={10} strokeWidth={2.5} className="inline mr-0.5" />
              {overdue} overdue
            </span>
          )}
        </button>
        {isManager && (
          <button
            data-testid="dash-backorder-add-btn"
            onClick={() => {
              setEditing(null);
              setFormOpen(true);
            }}
            className="nb-btn nb-btn-primary px-3 py-1.5 text-sm flex items-center gap-1"
          >
            <Plus size={14} strokeWidth={2.5} /> New
          </button>
        )}
      </div>

      {isExpanded &&
        (open === 0 ? (
        <div className="text-sm text-[#607087] italic text-center py-4">
          Nothing on backorder. {isManager && "Tap + New to flag one."}
        </div>
      ) : (
        <ul className="flex flex-col gap-2">
          {data.items.map((b) => {
            const isOverdue = b.is_overdue;
            return (
              <li
                key={b.id}
                data-testid={`backorder-row-${b.id}`}
                className={`border-2 rounded-xl p-3 flex items-center gap-3 ${
                  isOverdue ? "bg-[#FCE4E4] border-[#8A1F1F]" : "bg-white border-black"
                }`}
              >
                <div className="flex-1 min-w-0">
                  <div className="font-display text-base truncate">
                    {b.stock_name || "—"}
                  </div>
                  <div className="text-xs text-[#607087] mt-0.5 flex flex-wrap gap-2 items-center">
                    <span className="font-mono-num font-semibold">
                      {b.qty_expected} {b.stock_unit || ""}
                    </span>
                    {b.eta && (
                      <span
                        className={`${
                          isOverdue ? "text-[#8A1F1F] font-bold" : ""
                        }`}
                      >
                        {isOverdue ? "Overdue " : "ETA "} {b.eta}
                      </span>
                    )}
                    {b.supplier_name && <span>· {b.supplier_name}</span>}
                    {b.po_ref && <span>· {b.po_ref}</span>}
                  </div>
                  {b.notes && (
                    <div className="text-xs text-[#607087] mt-1 italic truncate">
                      {b.notes}
                    </div>
                  )}
                </div>
                <button
                  data-testid={`backorder-receive-${b.id}`}
                  onClick={() =>
                    nav(`/receive?backorder_id=${b.id}&stock_id=${b.stock_id}`)
                  }
                  className="nb-btn nb-btn-success px-3 py-2 text-xs flex items-center gap-1 shrink-0"
                >
                  <PackagePlus size={12} strokeWidth={2.5} /> Receive
                </button>
                {isManager && (
                  <>
                    <button
                      data-testid={`backorder-edit-${b.id}`}
                      onClick={() => {
                        setEditing(b);
                        setFormOpen(true);
                      }}
                      className="nb-btn bg-white p-2 shrink-0"
                      aria-label="edit"
                    >
                      <Edit3 size={14} strokeWidth={2.5} />
                    </button>
                    <button
                      data-testid={`backorder-cancel-${b.id}`}
                      onClick={() => cancel(b)}
                      className="nb-btn bg-white p-2 shrink-0"
                      aria-label="cancel"
                    >
                      <X size={14} strokeWidth={2.5} />
                    </button>
                  </>
                )}
              </li>
            );
          })}
        </ul>
      ))}

      <BackorderFormDialog
        open={formOpen}
        onClose={() => setFormOpen(false)}
        editing={editing}
        onSaved={load}
      />
    </div>
  );
};

export default BackordersWidget;
